import {
	Breadcrumb,
	NoResults,
	TagModel,
	useConfirmationDialog
} from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { TagViewModel } from "client/Api/Tags/tags";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubItemCategoryModel,
	HubItemHubSidebarNodeModel,
	HubDataSource
} from "client/Api/Hub/hub";
import { ContentWithRelevant } from "../../ContentWithRelevant";
import { MediaContentModel } from "../../HubDetailsLayout";
import {
	ActiveFilter,
	HubDialogContext,
	HubItemIsNewFilter,
	listHubImageUrlWithAttributions,
	ImageUrlWithAttribution
} from "../../common";
import {
	get,
	getReactionCounts,
	getReactions,
	getStatistics,
	KnowledgeItemDetailsViewModel,
	KnowledgeItemPreviewItemModel,
	KnowledgeItemStatisticsModel,
	registerView
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { RelevantItems } from "./RelevantItems";
import {
	getHubImageUrl,
	HubCommentModel,
	HubLinkedDocumentViewModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemMediaDetailsViewModel,
	HubItemViewLocation
} from "client/Api/Hub/common.details";
import { TimeZone } from "client/DateAndTime/timezones";
import { EditDialog } from "../../Management/KnowledgeItemsManagement/EditKnowledgeItemDialog/EditKnowledgeItemDialog";
import { useImageViewer } from "../../useImageViewer";
import { HttpRequestError } from "client/Api/httpClient";
import {
	KnowledgeItemContent,
	KnowledgeItemContentDataProps,
	KnowledgeItemContentModeProps,
	KnowledgeItemContentStatisticsProps
} from "./KnowledgeItemContent";
import { never } from "client_legacy/ndaw/errors";
import styles from "./Details.module.less";
import { HubEvent, publish } from "../../events";
import { useViewersDialog } from "../../ViewersDialog/ViewersDialog";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import {
	KnowledgeItemBreadcrumbSegment,
	KnowledgeItemClickableBreadcrumbSegment,
	useKnowledgeItemBreadcrumb,
	getSegmentTrackingId
} from "../useKnowledgeItemBreadcrumb";
import { HubCommentSection, HubCommentSectionService } from "../../HubCommentSection/HubCommentSection";
import { CommentThread } from "../../HubCommentSection/CommentThread";
import { getRelatedCommentThread } from "client/Api/Hub/KnowledgeItems/knowledgeItemComments";
import { getKnowledgeItemCommentService } from "./getKnowledgeItemCommentService";
import { HubItemCommentSection } from "../../HubItemCommentSection/HubItemCommentSection";
import { useViewSpecificHandlers } from "../../useViewSpecificHandlers";
import { useReactionsDialog } from "client/Features/Reactions/ReactionsDialog/ReactionsDialog";
import classNames from "classnames";

export type DetailsModeProps = ConditionalProps<
	{
		hierarchy: readonly HubItemHubSidebarNodeModel[];
		selectedCategoryId: number | null;
		activeFilter: ActiveFilter | null;
		onActiveFilterChange(filter: ActiveFilter): void;
		searchTerm: VersionedValue<string>;
		sorting: DataSourceSortableState<KnowledgeItemPreviewItemModel>;
		isNewFilter: HubItemIsNewFilter;
		onBack: () => void;
		onNavigateToItem(id: number): void;
		onCategoryCreated: () => void;
		onItemCategoryChange: (id: OptionalModel<number | null>) => void;
	},
	"mode",
	"full",
	"details-only"
>;

export type DetailsProps = {
	id: number;
	getCategory: (categoryId: number | nil) => HubItemCategoryModel | undefined;
	onSelectCategory: (categoryId: number) => void;
	onBreadcrumbNavigation: (segment: KnowledgeItemClickableBreadcrumbSegment) => void;
	viewLocation: HubItemViewLocation.Hub | HubItemViewLocation.Dialog;
	deepLink?: KnowledgeItemDetailsDeepLink;
}
	& DetailsModeProps;

export const Details = (props: DetailsProps): ReactElement => {
	const {
		id,
		hierarchy,
		getCategory,
		mode,
		viewLocation,
		activeFilter,
		onActiveFilterChange,
		onBack,
		onCategoryCreated,
		onNavigateToItem,
		onSelectCategory,
		onBreadcrumbNavigation,
		onItemCategoryChange,
		searchTerm,
		selectedCategoryId,
		sorting,
		isNewFilter,
		deepLink
	} = props;

	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");
	const [item, setItem] = useState<KnowledgeItemDetailsViewModel | null>(null);
	const [commentOptions, setCommentOptions] = useState<OptionalModel<HubCommentOptionsModel> | null>(null);
	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);
	const [statistics, setStatistics] = useState<KnowledgeItemStatisticsModel | undefined>();
	const [idToEdit, setIdToEdit] = useState<number | null>(null);

	const [commentsState, setCommentsState] = useState<CommentsState>(
		(): CommentsState => deepLink?.kind === "comment"
			? {
				kind: "comment-thread",
				commentId: deepLink.commentId
			}
			: {
				kind: "all-comments",
				shouldReserveSpace: false
			}
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const userTimezone: TimeZone = useUserTimezone();
	const shouldRequestHierarchy: boolean = isNil(hierarchy);

	const { setViewSpecificHandlers } = useContext(HubDialogContext);

	const [viewersDialog, openViewersDialog] = useViewersDialog();
	const [reactionsDialog, openReactionsDialog] = useReactionsDialog();
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();

	const imageViewerItems = useMemo(
		(): readonly ImageUrlWithAttribution[] => {
			return listHubImageUrlWithAttributions(HubDataSource.KnowledgeItem, item);
		},
		[item]
	);

	const [imageViewerDialog, viewImageHandler] = useImageViewer(imageViewerItems);

	const linkedDocuments: readonly HubLinkedDocumentViewModel[] = item?.linkedDocuments
		?? EMPTY_ARRAY;

	const category = useMemo(
		(): HubItemCategoryModel | undefined => item
			? getCategory(item.categoryId)
			: undefined,
		[getCategory, item]
	);

	const breadCrumb: readonly KnowledgeItemBreadcrumbSegment[] = useKnowledgeItemBreadcrumb(
		hierarchy
			? {
				hierarchy,
				leaf: {
					kind: "item",
					categoryId: category?.id
				}
			}
			: {
				itemBreadcrumb: item
					? item.breadcrumb
					: { isSet: false }
			}
	);

	useViewSpecificHandlers({
		isEditingMobileComment,
		openConfirmationDialog,
		onBack: mode === "full"
			? onBack
			: undefined
	});

	useEffect(
		(): (() => void) | undefined => {
			if (!onBack) {
				return undefined;
			}

			setViewSpecificHandlers({
				backHandler: onBack
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[setViewSpecificHandlers, onBack]
	);

	const registerViewAndFetchStatisticsHandler = useCallback(
		async (itemId: number, signal: AbortSignal): Promise<void> => {
			try {
				await registerView(itemId, signal);
				const statistics: KnowledgeItemStatisticsModel = await getStatistics(itemId, signal);
				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				if (!(e instanceof HttpRequestError && e.statusCode === 404)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
		},
		[alertManager]
	);

	const reloadStatistics = useCallback(
		async (): Promise<void> => {
			try {
				const statistics: KnowledgeItemStatisticsModel = await getStatistics(id, globalCancellation);
				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[globalCancellation, id]
	);

	const allCommentViewHandler = useCallback(
		(): void => {
			setCommentsState({
				kind: "all-comments",
				shouldReserveSpace: true
			});
		},
		[]
	);

	const getRelatedCommentThreadHandler = useCallback(
		(commentId: number, signal?: AbortSignal): Promise<readonly HubCommentModel[]> => {
			return getRelatedCommentThread(
				id,
				commentId,
				signal
			);
		},
		[id]
	);

	const fetchItem = useCallback(
		async (
			id: number,
			signal: AbortSignal,
			isFirstLoad: boolean = false
		): Promise<void> => {
			setItem(null);
			setLoadingStatus("loading");

			try {
				const {
					item,
					commentOptions
				} = await get(
					id,
					shouldRequestHierarchy,
					signal
				);

				setItem(item);
				setCommentOptions(commentOptions);
				setLoadingStatus("found");

				if (isFirstLoad) {
					const {
						isNew,
						id: itemId,
						categoryId
					} = item;

					if (isNew) {
						publish(
							HubEvent.IsNewStatusCleared,
							{
								kind: HubDataSource.KnowledgeItem,
								itemId,
								categoryId
							}
						);
					}

					registerViewAndFetchStatisticsHandler(itemId, signal);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				let loadingStatus: LoadingStatus = "error";

				if (e instanceof HttpRequestError && e.statusCode === 404) {
					loadingStatus = "not-found";
				} else {
					alertManager.showError(localize("_CannotOpenItem"));
				}

				setLoadingStatus(loadingStatus);
			}
		},
		[
			alertManager,
			shouldRequestHierarchy,
			registerViewAndFetchStatisticsHandler
		]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();

			fetchItem(
				id,
				cancellation.signal,
				true
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			fetchItem,
			id
		]
	);

	useEffect(
		(): (() => void) | void => {
			if (!item) {
				return;
			}

			onItemCategoryChange?.({
				isSet: true,
				value: category
					? category.id
					: null
			});

			return () => onItemCategoryChange?.({ isSet: false });
		},
		[
			category,
			item,
			onItemCategoryChange
		]
	);

	const tags = useMemo(
		(): readonly TagModel<number>[] => {
			return item?.tags
				.map((x): TagModel<number> => ({
					text: x.name,
					value: x.id,
					color: "secondary"
				})) ?? [];
		},
		[item?.tags]
	);

	const afterSaveHandler = useCallback(
		(): void => {
			fetchItem(id, globalCancellation);
		},
		[
			id,
			globalCancellation,
			fetchItem
		]
	);

	const editHandler = useCallback(
		(): void => {
			setIdToEdit(
				ensureNotNil(item?.id)
			);
		},
		[item?.id]
	);

	const closeEditDialogHandler = useMemo(
		(): ((wasAnyCategoryCreated: boolean) => void) | undefined => {
			if (!onCategoryCreated) {
				return undefined;
			}

			return (wasAnyCategoryCreated): void => {
				setIdToEdit(null);

				if (wasAnyCategoryCreated) {
					onCategoryCreated();
				}
			};
		},
		[onCategoryCreated]
	);

	const breadcrumbNavigationHandler = useCallback(
		(segment: KnowledgeItemBreadcrumbSegment): void => {
			switch(segment.kind) {
				case "item":
					throw new Error("Article is not clickable");
				case "category":
				case "group":
				case "overview":
					onBreadcrumbNavigation(segment);
					break;
				default:
					never(segment);
			}
		},
		[onBreadcrumbNavigation]
	);

	const knowledgeItemCommentService = useMemo(
		(): HubCommentSectionService => {
			return getKnowledgeItemCommentService(id);
		},
		[id]
	);

	const dataProps = useMemo(
		(): KnowledgeItemContentDataProps => {
			if (!item || !commentOptions) {
				return {
					hasData: false
				};
			}

			return {
				hasData: true,
				title: item.title,
				category,
				media: convertMedia(item.media),
				layout: item.layout,
				isEditable: item.isEditable,
				isPinned: isNotNil(item.pinDate),
				createdBy: item.author,
				modifiedDate: item.modifiedDate,
				modifiedBy: item.modifiedBy,
				publicationDate: item.publicationDate,
				tags,
				areCommentsEnabled: commentOptions.isSet
			};
		},
		[
			category,
			commentOptions,
			item,
			tags
		]
	);

	const isAllowedToLookUpViewers: boolean = isNotNil(item)
		&& item.isEditable;

	const statisticsProps = useMemo(
		(): KnowledgeItemContentStatisticsProps => {
			if (!statistics) {
				return {
					hasStatistics: false
				};
			}

			return {
				hasStatistics: true,
				statistics,
				onOpenViewersDialog: isAllowedToLookUpViewers
					? () => openViewersDialog({
						count: statistics.viewsCount,
						itemId: id,
						itemKind: HubItemKind.KnowledgeItem
					})
					: undefined,
				onOpenReactionsDialog: () => openReactionsDialog({
					itemId: id,
					getReactions: getReactions,
					getReactionCounts: getReactionCounts
				}),
				onStatisticsRetrieved: setStatistics
			};
		},
		[
			id,
			isAllowedToLookUpViewers,
			openReactionsDialog,
			openViewersDialog,
			statistics
		]
	);

	const modeProps = useMemo(
		(): KnowledgeItemContentModeProps => {
			switch (mode) {
				case "full":
					return {
						mode: "full",
						activeFilter,
						onBack,
						onEdit: editHandler,
						onSelectAuthor: (authorId: number, authorName: string): void => {
							onActiveFilterChange({
								kind: "author",
								value: {
									id: authorId,
									name: authorName
								}
							});
						},
						onSelectCategory: onSelectCategory,
						onTagClick: (id: number | null): void => {
							const tag: TagViewModel = ensureNotNil(
								item?.tags
									.find(x => x.id === id)
							);

							onActiveFilterChange({
								kind: "tag",
								value: {
									...tag,
									itemKind: HubItemKind.KnowledgeItem
								}
							});
						}
					};
				case "details-only":
					return {
						mode: "details-only",
						onSelectCategory: onSelectCategory
					};
				default:
					never(mode);
			}
		},
		[
			activeFilter,
			editHandler,
			item?.tags,
			mode,
			onActiveFilterChange,
			onBack,
			onSelectCategory
		]
	);

	const commentsCount: number = statistics?.commentsCount
		.isSet
			? statistics.commentsCount
				.value
			: 0;

	const renderCommentsSection = (): ReactElement | null => {
		if (!item || !commentOptions) {
			return null;
		}

		if (!commentOptions.isSet) {
			return (
				<HubCommentSection.NoComments />
			);
		}

		return (
			<div className={styles.commentsSectionContainer}>
				{commentsState.kind === "comment-thread"
					? (
						<CommentThread
							commentId={commentsState.commentId}
							onStatisticsUpdate={reloadStatistics}
							onMobileCommentIsEditingChanged={setIsEditingMobileComment}
							options={commentOptions.value}
							timezone={userTimezone}
							onAllCommentView={allCommentViewHandler}
							allCommentsCount={commentsCount}
							getRelatedCommentThread={getRelatedCommentThreadHandler}
							service={knowledgeItemCommentService}
						/>
					)
					: (
						<div
							className={
								classNames(
									{ [styles.withMinHeight]: commentsState.shouldReserveSpace }
								)
							}
						>
							<HubItemCommentSection
								kind={HubItemKind.KnowledgeItem}
								onStatisticsUpdate={reloadStatistics}
								onMobileCommentIsEditingChanged={setIsEditingMobileComment}
								timezone={userTimezone}
								options={commentOptions.value}
								allCommentsCount={commentsCount}
								service={knowledgeItemCommentService}
							/>
						</div>
					)
				}
			</div>
		);
	};

	return (
		<>
			<ContentWithRelevant trackingId="knowledge-item-details">
				<ContentWithRelevant.Content hasBusyIndicator={loadingStatus === "loading"}>
					<div className={styles.breadcrumb}>
						<Breadcrumb
							segments={breadCrumb}
							onNavigate={breadcrumbNavigationHandler}
							trackingId="breadcrumb"
							getSegmentTrackingId={getSegmentTrackingId}
						/>
					</div>
					<KnowledgeItemContent
						{...dataProps}
						{...statisticsProps}
						{...modeProps}
						linkedDocumentsMode="view"
						linkedDocuments={linkedDocuments}
						onChangeIsLoading={(isLoading) => setLoadingStatus(isLoading ? "loading" : "found")}
						isInteractive
						id={id}
						timezone={userTimezone}
						onViewImage={viewImageHandler}
					/>

					{renderCommentsSection()}

					{loadingStatus === "not-found" && (
						<NoResults
							{...(onBack
								? {
									retryText: localize("_BackToOverview"),
									onRetry: onBack
								}
								: {}
							)}
							icon="book"
							title={localize("_ItemNotFound")}
							message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
							trackingId="knowledge-item-not-found"
							height="fill"
						/>
					)}
				</ContentWithRelevant.Content>

				{(mode === "full" && loadingStatus !== "not-found") && (
					<ContentWithRelevant.Relevant title={localize("_OtherRelatedArticles")}>
						<RelevantItems
							item={item}
							viewsCount={statistics?.viewsCount}
							getCategory={getCategory}
							activeFilter={activeFilter}
							onActiveFilterChange={onActiveFilterChange}
							selectedCategoryId={selectedCategoryId}
							onSelectedCategoryIdChange={onSelectCategory}
							searchTerm={searchTerm}
							sorting={sorting}
							onNavigateToItem={onNavigateToItem}
							timezone={userTimezone}
							isNewFilter={isNewFilter}
						/>
					</ContentWithRelevant.Relevant>
				)}
				{viewLocation === HubItemViewLocation.Hub && (
					<AiAssistantFabSpacing />
				)}
			</ContentWithRelevant>

			{imageViewerDialog}
			{viewersDialog}
			{reactionsDialog}
			{confirmationDialogElement}

			{isNotNil(idToEdit) && isNotNil(closeEditDialogHandler) && (
				<EditDialog
					mode={{
						kind: "edit",
						itemId: idToEdit
					}}
					onSave={afterSaveHandler}
					onClose={closeEditDialogHandler}
					onBack={closeEditDialogHandler}
				/>
			)}
		</>
	);
};
Details.displayName = "Details";

export type KnowledgeItemDetailsDeepLink = {
	kind: "comment";
	commentId: number;
} | {
	kind: "comments";
};

type LoadingStatus =
	| "loading"
	| "found"
	| "not-found"
	| "error";

type CommentsState = {
	kind: "all-comments";
	shouldReserveSpace?: boolean;
} | {
	kind: "comment-thread";
	commentId: number;
};

function convertMedia(media: HubItemMediaDetailsViewModel): MediaContentModel {
	switch (media.mediaKind) {
		case HubItemMediaKind.Image:
			return {
				mediaKind: HubItemMediaKind.Image,
				imageUrl: getHubImageUrl(HubDataSource.KnowledgeItem, media.imageId),
				attribution: media.attribution
			};
		default:
			return media;
	}
}